import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getReadyToPickOrders, scanResolve } from "@/lib/operations.functions";
import { listOpenPickJobs, startJobSession, type PickJobRow } from "@/lib/pick-jobs.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { ContextualTip } from "@/components/kb/contextual-tip";
import { ScanInput } from "@/components/ui/scan-input";
import { EmptyState } from "@/components/ui/empty-state";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScanLine, ChevronRight, PackageOpen, Truck, ClipboardList, PlayCircle, Layers, Boxes, Package } from "lucide-react";
import { useScanner, scannerBeep } from "@/hooks/use-scanner";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/scan")({ component: ScanIndex });

type LastResult =
  | { kind: "order"; id: string; label: string; status?: string }
  | { kind: "sku"; id: string; label: string; name?: string }
  | { kind: "asn"; id: string; label: string }
  | null;

function ScanIndex() {
  const { tenantId, facilityId } = useScope();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [last, setLast] = useState<LastResult>(null);
  const fetchReady = useServerFn(getReadyToPickOrders);
  const resolveCode = useServerFn(scanResolve);
  const listJobsFn = useServerFn(listOpenPickJobs);
  const startJobFn = useServerFn(startJobSession);

  const { data: orders = [] } = useQuery({
    queryKey: ["scan-orders", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () =>
      fetchReady({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const { data: jobs } = useQuery({
    queryKey: ["open-pick-jobs", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listJobsFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });

  const startJob = async (job: PickJobRow) => {
    try {
      const res = await runServerFn(() => startJobFn({ data: { jobId: job.id } }), "Starting job");
      qc.invalidateQueries({ queryKey: ["open-pick-jobs"] });
      navigate({ to: "/app/pick/session/$sessionId", params: { sessionId: res.sessionId } });
    } catch {
      // toast handled
    }
  };

  async function resolve(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed || !tenantId) return;

    const order = orders.find((o) => o.order_number.toLowerCase() === trimmed.toLowerCase());
    if (order) {
      setLast({ kind: "order", id: order.id, label: order.order_number, status: order.status });
      toast.success(`Order ${order.order_number}`);
      return;
    }

    const hit = await resolveCode({ data: { tenantId, code: trimmed } });
    if (hit.kind === "order") {
      setLast({ kind: "order", id: hit.id, label: hit.order_number, status: hit.status });
      toast.success(`Order ${hit.order_number}`);
      return;
    }
    if (hit.kind === "sku") {
      setLast({ kind: "sku", id: hit.id, label: hit.sku_code, name: hit.name });
      toast.success(`SKU ${hit.sku_code}`);
      return;
    }
    if (hit.kind === "asn") {
      setLast({ kind: "asn", id: hit.id, label: hit.reference });
      toast.success(`ASN ${hit.reference}`);
      return;
    }

    scannerBeep(false);
    setLast(null);
    toast.error(`No match for "${trimmed}"`);
  }

  // Hardware HID scanner — captures keystrokes anywhere on the page
  useScanner({ enabled: !!tenantId, onScan: resolve });

  return (
    <>
      <PageHeader
        title="Scan Station"
        description="Scan an order, SKU, barcode, or ASN. Hardware scanners and the mobile camera both work."
      />
      <div className="px-4 sm:px-6 py-6 max-w-3xl mx-auto space-y-6">
        <ContextualTip tipKey="scan_intro" />
        <ScanInput onScan={resolve} size="lg" placeholder="Scan or type a barcode…" />

        {last && (
          <div className="rounded-xl border-2 border-scan/40 bg-scan/5 p-5 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="h-12 w-12 rounded-lg bg-scan text-scan-foreground flex items-center justify-center shrink-0">
                {last.kind === "order" ? <ClipboardList className="h-6 w-6" /> :
                 last.kind === "sku" ? <PackageOpen className="h-6 w-6" /> :
                 <Truck className="h-6 w-6" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">{last.kind}</div>
                <div className="text-2xl font-bold tracking-tight font-mono truncate">{last.label}</div>
                {"name" in last && last.name && <div className="text-sm text-muted-foreground truncate">{last.name}</div>}
                {"status" in last && last.status && (
                  <div className="mt-2"><StatusPill tone={statusTone(last.status)} dot>{last.status}</StatusPill></div>
                )}
              </div>
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {last.kind === "order" && (
                <>
                  <Button size="touch" onClick={() => navigate({ to: "/app/orders/$orderId", params: { orderId: last.id } })}>
                    Open order <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button size="touch" variant="outline" onClick={() => navigate({ to: "/app/pack/$orderId", params: { orderId: last.id } })}>
                    Pack
                  </Button>
                </>
              )}
              {last.kind === "sku" && (
                <Button size="touch" onClick={() => navigate({ to: "/app/inventory/skus" })}>
                  View SKU catalog <ChevronRight className="h-4 w-4" />
                </Button>
              )}
              {last.kind === "asn" && (
                <Button size="touch" onClick={() => navigate({ to: "/app/receiving/$asnId", params: { asnId: last.id } })}>
                  Open ASN <ChevronRight className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold tracking-tight">Jobs</h2>
            <span className="text-xs text-muted-foreground">
              {((jobs?.bulk_batch.length ?? 0) + (jobs?.pick_to_bin.length ?? 0) + (jobs?.order_by_order.length ?? 0))} open
            </span>
          </div>
          {!jobs || (jobs.bulk_batch.length + jobs.pick_to_bin.length + jobs.order_by_order.length) === 0 ? (
            <EmptyState
              icon={ScanLine}
              title="No Jobs Yet"
              description="Approve grouped suggestions on the Pick screen to create jobs."
            />
          ) : (
            <div className="space-y-4">
              <JobSection title="Bulk / Batch Jobs" icon={<Layers className="h-4 w-4" />} jobs={jobs.bulk_batch} onStart={startJob} />
              <JobSection title="Pick to Cart Jobs" icon={<Boxes className="h-4 w-4" />} jobs={jobs.pick_to_bin} onStart={startJob} />
              <JobSection title="Order by Order Jobs" icon={<Package className="h-4 w-4" />} jobs={jobs.order_by_order} onStart={startJob} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function JobSection({
  title, icon, jobs, onStart,
}: { title: string; icon: React.ReactNode; jobs: PickJobRow[]; onStart: (j: PickJobRow) => void }) {
  if (jobs.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
        {icon}<span>{title}</span><Badge variant="secondary">{jobs.length}</Badge>
      </div>
      <ul className="space-y-2">
        {jobs.map((j) => (
          <li key={j.id} className="flex items-center justify-between gap-3 p-4 border rounded-lg bg-card">
            <div className="min-w-0">
              <div className="font-semibold truncate">{j.customer_name}</div>
              <div className="text-xs text-muted-foreground">
                {j.order_count} order{j.order_count === 1 ? "" : "s"}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill tone={statusTone(j.status)} dot>{j.status.replace("_", " ")}</StatusPill>
              <Button size="sm" onClick={() => onStart(j)}>
                <PlayCircle className="h-4 w-4 mr-1" />{j.status === "in_progress" ? "Resume" : "Start"}
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
